import React, { useMemo, useState } from 'react';
import {
  loadConnectAndInitialize,
  type StripeConnectInstance,
} from '@stripe/connect-js';
import {
  ConnectComponentsProvider,
  ConnectNotificationBanner,
  ConnectPayments,
  ConnectDisputesList,
  ConnectAccountManagement,
} from '@stripe/react-connect-js';
import env from '@/config/env';
import { stripeAdminApi, type StripeDashboardMode } from '../api/stripeAdminApi';
import { ContractsPaymentsTab } from './ContractsPaymentsTab';
import { PlatformFeesTab } from './PlatformFeesTab';
import { PayoutReconciliationTab } from './PayoutReconciliationTab';

type TabKey =
  | 'contracts'
  | 'payments'
  | 'platform-fees'
  | 'reconciliation'
  | 'disputes'
  | 'account';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'contracts', label: 'Contrats' },
  { key: 'payments', label: 'Paiements' },
  { key: 'platform-fees', label: 'Frais de plateforme' },
  { key: 'reconciliation', label: 'Rapprochement' },
  { key: 'disputes', label: 'Litiges' },
  { key: 'account', label: 'Mon compte Stripe' },
];

/**
 * Onglet « Litiges » pour le vendeur seulement. L'admin les voit et y répond depuis
 * l'onglet Paiements (sous-onglet « Litiges », qui n'apparaît qu'avec la gestion des
 * litiges). Le vendeur ne fait que les consulter : Affinicar porte les pertes.
 */
const tabsFor = (mode: StripeDashboardMode) =>
  mode === 'admin' ? TABS.filter((tab) => tab.key !== 'disputes') : TABS;

/**
 * Dashboard Stripe d'un compte connecté (résolu via societyId). Composants Connect
 * embarqués pour paiements (et litiges)/payouts/rapprochement/compte, + onglets
 * maison « Contrats » et « Frais de plateforme ». En `self`, la session est en
 * lecture seule (aucun self-refund / self-payout) ; en `admin`, remboursement et
 * litige actifs.
 */
export const StripeConnectDashboard: React.FC<{
  societyId: string;
  /** `self` (défaut) : vendeur, lecture seule. `admin` : administrateur, actions activées. */
  mode?: StripeDashboardMode;
}> = ({ societyId, mode = 'self' }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('contracts');
  const [initError, setInitError] = useState<string | null>(null);

  const connectInstance: StripeConnectInstance | null = useMemo(() => {
    try {
      const publishableKey = env.STRIPE_PUBLIC_KEY;
      if (!publishableKey || publishableKey === 'false') {
        setInitError('Clé Stripe non configurée pour ce domaine.');
        return null;
      }
      setInitError(null);
      return loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () => {
          const resp = await stripeAdminApi.createAccountSession(societyId, mode);
          if (!resp.success || !resp.clientSecret) {
            throw new Error(resp.error || 'Impossible de créer la session Stripe');
          }
          return resp.clientSecret;
        },
        // Aligne police + couleur d'accent des composants Stripe embarqués sur
        // l'app (affinicar-primary #8074af, stack de police système), pour que les
        // listes Stripe et l'onglet « Contrats & paiements » partagent la même
        // identité visuelle. L'API appearance ne pilote que ces variables — la
        // structure interne des composants reste celle de Stripe.
        appearance: {
          variables: {
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
            colorPrimary: '#8074af',
            colorText: '#262522',
            colorBackground: '#ffffff',
            borderRadius: '8px',
            buttonPrimaryColorBackground: '#8074af',
            buttonPrimaryColorText: '#ffffff',
          },
        },
      });
    } catch (err: any) {
      setInitError(err?.message || "Erreur d'initialisation Stripe");
      return null;
    }
  }, [societyId, mode]);

  if (initError) {
    return (
      <div className="tw-p-6 tw-text-center tw-text-red-700">
        <p>Erreur d'initialisation du dashboard Stripe&nbsp;:</p>
        <p className="tw-font-mono tw-text-sm tw-mt-2">{initError}</p>
      </div>
    );
  }

  if (!connectInstance) {
    return <div className="tw-p-6 tw-text-center">Chargement…</div>;
  }

  return (
    <ConnectComponentsProvider connectInstance={connectInstance}>
      <div className="tw-mb-4">
        <ConnectNotificationBanner />
      </div>

      <div className="tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-gray-200 tw-mb-4">
        {tabsFor(mode).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={
              'tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-border-b-2 tw--mb-px ' +
              (activeTab === tab.key
                ? 'tw-border-affinicar-primary tw-text-affinicar-primary'
                : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700')
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'contracts' && <ContractsPaymentsTab societyId={societyId} />}
        {activeTab === 'payments' && <ConnectPayments />}
        {activeTab === 'platform-fees' && <PlatformFeesTab societyId={societyId} mode={mode} />}
        {activeTab === 'reconciliation' && (
          <PayoutReconciliationTab societyId={societyId} mode={mode} />
        )}
        {activeTab === 'disputes' && <ConnectDisputesList />}
        {activeTab === 'account' && <ConnectAccountManagement />}
      </div>
    </ConnectComponentsProvider>
  );
};
